import { useCallback, useEffect, useMemo, useState } from "react"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { Tabs } from "components/navigation/Tabs"
import { TopBar } from "components/navigation/TopBar"

import { BrandSiteProvider } from "features/cms-seo/BrandSiteContext"
import { CreatePageModal } from "features/cms-seo/CreatePageModal"
import { PageEditor } from "features/cms-seo/PageEditor"
import { CategorySeoTab } from "features/cms-seo/tab-contents/CategorySeoTab"
import { GameSeoTab } from "features/cms-seo/tab-contents/GameSeoTab"
import { MediaTab } from "features/cms-seo/tab-contents/MediaTab"
import { PagesTab } from "features/cms-seo/tab-contents/PagesTab"
import { PromotionsTab } from "features/cms-seo/tab-contents/PromotionsTab"
import { RedirectsTab } from "features/cms-seo/tab-contents/RedirectsTab"
import { RouteSeoTab } from "features/cms-seo/tab-contents/RouteSeoTab"
import { SeoRulesTab } from "features/cms-seo/tab-contents/SeoRulesTab"
import { SitemapRobotsTab } from "features/cms-seo/tab-contents/SitemapRobotsTab"
import { SportsbookSeoTab } from "features/cms-seo/tab-contents/SportsbookSeoTab"

import { useProps } from "contexts/PropsContext"

import { cmsSeoService } from "services/cms-seo/cms-seo.service"
import { CmsPageListItem, CmsPageType } from "services/cms-seo/type"

type ListTab =
  | "pages"
  | "promotions"
  | "routes"
  | "categories"
  | "games"
  | "sports"
  | "rules"
  | "redirects"
  | "media"
  | "sitemap"

/** Enough to fill the parent and author pickers without a dedicated endpoint. */
const CATALOGUE_PAGE_SIZE = 200

const BRAND = process.env.REACT_APP_TENANT || "Brand"

/** The card every other view in the panel sits in. */
const CARD_CLASS =
  "bg-white dark:bg-[#0C0C1A] rounded-xl border border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.1)] shadow-sm"

export function CmsSeo() {
  const { t } = useTranslation("translation", { useSuspense: false })
  const { darkMode } = useProps()

  const [tab, setTab] = useState<ListTab>("pages")
  const [editingId, setEditingId] = useState<string | null>(null)
  const [createType, setCreateType] = useState<CmsPageType | null>(null)
  const [catalogue, setCatalogue] = useState<CmsPageListItem[]>([])
  const [listVersion, setListVersion] = useState(0)

  const loadCatalogue = useCallback(async () => {
    const data = await cmsSeoService.listPages({ page: 1, pageSize: CATALOGUE_PAGE_SIZE })
    setCatalogue(data.items)
  }, [])

  useEffect(() => {
    loadCatalogue()
  }, [loadCatalogue, listVersion])

  const authors = useMemo(
    () => Array.from(new Set(catalogue.map((row) => row.author).filter((name): name is string => Boolean(name)))).sort(),
    [catalogue]
  )

  const refreshLists = () => setListVersion((version) => version + 1)

  const tabs = [
    { key: "pages", label: t("cmsSeo.tab.pages", { defaultValue: "Pages" }) },
    { key: "promotions", label: t("cmsSeo.tab.promotions", { defaultValue: "Promotions & News" }) },
    { key: "routes", label: t("cmsSeo.tab.routes", { defaultValue: "Built-in routes" }) },
    { key: "categories", label: t("cmsSeo.tab.categories", { defaultValue: "Game categories" }) },
    { key: "games", label: t("cmsSeo.tab.games", { defaultValue: "Games" }) },
    { key: "sports", label: t("cmsSeo.tab.sports", { defaultValue: "Sportsbook" }) },
    { key: "rules", label: t("cmsSeo.tab.rules", { defaultValue: "SEO Rules" }) },
    { key: "redirects", label: t("cmsSeo.tab.redirects", { defaultValue: "Redirects" }) },
    { key: "media", label: t("cmsSeo.tab.media", { defaultValue: "Media" }) },
    { key: "sitemap", label: t("cmsSeo.tab.sitemap", { defaultValue: "Sitemap & Robots" }) }
  ]

  return (
    <BrandSiteProvider>
      <div className={clsx(darkMode && "dark")}>
      {/* `max-w-full`, not `100vw`: the column is the viewport minus the sidebar. */}
      <div className="w-full max-w-full p-4 pt-0 sm:p-8 sm:pt-0">
        <TopBar title={t("cmsSeo.title", { defaultValue: "CMS Pages" })} />

        <div className={clsx("mt-4", CARD_CLASS)}>
          {editingId ? (
            <PageEditor
              key={editingId}
              pageId={editingId}
              brand={BRAND}
              parentCandidates={catalogue}
              authors={authors}
              onBack={() => setEditingId(null)}
              onSaved={refreshLists}
              onNavigate={setEditingId}
            />
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 pt-4 sm:px-6 sm:pt-6">
                <Tabs tabs={tabs} activeTab={tab} onChange={(key) => setTab(key as ListTab)} variant="pills" />

                {/* A page edited against the wrong brand never appears on the site. */}
                <span className="rounded-lg border border-[rgba(0,0,0,0.05)] px-3 py-1.5 text-xs text-gray-500 dark:border-[rgba(255,255,255,0.1)] dark:text-gray-400">
                  {t("cmsSeo.brand", { defaultValue: "Brand" })}:{" "}
                  <b className="font-semibold text-gray-700 dark:text-gray-200">{BRAND}</b>
                </span>
              </div>

              {tab === "pages" && (
                <PagesTab key={`pages-${listVersion}`} onOpen={setEditingId} onCreate={() => setCreateType("page")} />
              )}
              {tab === "promotions" && <PromotionsTab />}
              {tab === "routes" && <RouteSeoTab />}
              {tab === "categories" && <CategorySeoTab />}
              {tab === "games" && <GameSeoTab />}
              {tab === "sports" && <SportsbookSeoTab />}
              {tab === "rules" && <SeoRulesTab />}
              {tab === "redirects" && <RedirectsTab />}
              {tab === "media" && <MediaTab />}
              {tab === "sitemap" && <SitemapRobotsTab />}
            </>
          )}
        </div>
      </div>

      <CreatePageModal
        open={createType !== null}
        defaultType={createType ?? "page"}
        defaultLocale="en"
        onClose={() => setCreateType(null)}
        onCreated={(id) => {
          setCreateType(null)
          refreshLists()
          setEditingId(id)
        }}
        />
      </div>
    </BrandSiteProvider>
  )
}
